import type { TradeDirection } from '@einhaus/project314-types';
import { formatTradeLogicV2ExitCandidateLabel } from './formatTradeLogicV2ExitCandidateLabel';

export interface TradeLogicV2ExitDisplayRule {
    action: string;
    condition: string;
    /** Position side the rule is gated to; null when it applies to both. */
    direction: TradeDirection | null;
    /** Scannable one-liner for chips; `action` plus `condition` carry the detail. */
    headline: string;
    priority: number | null;
    reason: string | null;
}

export interface TradeLogicV2ExitDisplaySummary {
    label: string;
    description: string | null;
    source: string;
    rules: TradeLogicV2ExitDisplayRule[];
}

export interface TradeLogicV2ExitDefinitionSummary {
    definitionId: number;
    name: string;
    latestCompiledVersion: number | null;
}

export interface TradeLogicV2ExitSummaryViewModel {
    candidateLabel: string;
    description: string | null;
    /** Rule-set name and version, kept as small print now that the rules themselves are shown. */
    definitionLabel: string;
    /** Used when a candidate has no readable rules, so the cell still says something. */
    displayLabel: string;
    rules: TradeLogicV2ExitDisplayRule[];
}

export const buildTradeLogicV2ExitSummary = ({
    definition,
    direction,
    exitCandidateDisplay,
    exitCandidateHash,
    exitCandidateId,
    strategyVersion,
    versionId
}: {
    definition: TradeLogicV2ExitDefinitionSummary | null;
    direction: TradeDirection | null;
    exitCandidateDisplay: TradeLogicV2ExitDisplaySummary | null;
    exitCandidateHash?: string | null;
    exitCandidateId?: number | null;
    strategyVersion: number | null;
    versionId: number | null;
}): TradeLogicV2ExitSummaryViewModel => {
    const definitionName = getDefinitionName(definition, versionId);
    const resolvedVersion = strategyVersion ?? definition?.latestCompiledVersion ?? null;
    const definitionLabel = resolvedVersion !== null ? `${definitionName} · v${resolvedVersion}` : definitionName;

    return {
        candidateLabel: formatTradeLogicV2ExitCandidateLabel({ exitCandidateId, exitCandidateHash }),
        definitionLabel,
        description: exitCandidateDisplay?.description ?? null,
        displayLabel: exitCandidateDisplay?.label?.trim() || definitionLabel,
        rules: filterRulesForDirection(exitCandidateDisplay?.rules ?? [], direction)
    };
};

export const normalizeDirection = (direction?: string | null): TradeDirection | null =>
    direction === 'long' || direction === 'short' ? direction : null;

/** A rule gated to the opposite direction can never fire for this trade, so showing it only misleads. */
export const filterRulesForDirection = (
    rules: TradeLogicV2ExitDisplayRule[],
    direction: TradeDirection | null
): TradeLogicV2ExitDisplayRule[] =>
    direction === null ? rules : rules.filter((rule) => rule.direction === null || rule.direction === direction);

export const getRuleKey = (rule: TradeLogicV2ExitDisplayRule, index: number): string =>
    `${rule.priority ?? index}-${rule.action}-${rule.condition}`;

export interface TradeLogicV2ExitRuleChip {
    headline: string;
    reason: string | null;
}

/**
 * Chips for the table cell. Headlines repeat when a rule exists once per direction and the
 * direction is unknown, and the same chip twice reads as a bug rather than as two rules.
 */
export const buildRuleChips = (rules: TradeLogicV2ExitDisplayRule[]): TradeLogicV2ExitRuleChip[] => {
    const chipsByHeadline = new Map<string, TradeLogicV2ExitRuleChip>();

    for (const rule of rules) {
        if (!chipsByHeadline.has(rule.headline)) chipsByHeadline.set(rule.headline, { headline: rule.headline, reason: rule.reason });
    }

    return [...chipsByHeadline.values()];
};

const getDefinitionName = (definition: TradeLogicV2ExitDefinitionSummary | null, versionId: number | null): string => {
    if (definition) return definition.name;
    if (versionId !== null) return `Trade Logic V2 exit version #${versionId}`;
    return 'Trade Logic V2 exit';
};
